import { PropsWithChildren, useMemo } from "react";
import { useAuth } from "react-oidc-context";

import { NoPermissionPage, getUserRoles } from "@shared";

import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";

// The roles the internal app grants any rights for. All other internal-realm principals (e.g. a
// future FSW-federated account whose rights were not assigned yet) get the "Keine Berechtigung"
// page. Volunteers are not listed: they are external-realm users working in the client portal (Kundenportal) and
// cannot authenticate against this app's realm at all.
const INTERNAL_APP_PERMISSIONS = [UserPermission.SulStaff, UserPermission.Supervisor];

/**
 * Renders the app only when the signed-in user holds an internal-app role. Sits between AuthGuard
 * and CurrentUserContextProvider so a role-less user never sees the app shell and never triggers
 * the current-user API call (which would only produce 403 error toasts).
 */
export const RequiredRoleGuard = ({ children }: PropsWithChildren) => {
  const auth = useAuth();

  // Memoized because getUserRoles decodes the full access token, and this guard wraps the whole
  // app — it re-renders on every auth state change, silent renews included.
  const roles = useMemo(() => getUserRoles(auth.user), [auth.user]);
  if (!INTERNAL_APP_PERMISSIONS.some((permission) => roles.includes(permission))) {
    return <NoPermissionPage />;
  }

  return <>{children}</>;
};
